import React, { useState } from 'react';
import {
	Alert,
	Form,
	Input,
	Tooltip,
} from 'antd';
import classNames from 'classnames';
import { FormInstance } from 'antd/lib/form';
import { trimString } from '@shared/utils/string';
import { DATE_AND_TIME } from '@shared/utils/constants';
import { toMoment } from '@shared/utils/date';
import type { GetVoyagesResponse } from '@api/features/voyages/getVoyages';
import { useAuth } from '@client/lib/auth';
import Select from '../Select';
import MultiCurrencyInput from '../MultiCurrencyInput';
import Button from '../Button';
import AccountCodeOverrideFormItem from '../AccountCodeOverrideFormItem';
import styles from './CreateEuExpenseForm.module.css';

const required = [{ required: true, message: 'Field is required' }];

const EuExpenseFormItems = ({
	form,
	voyages,
	extra,
}: {
	form: FormInstance;
	voyages: GetVoyagesResponse['rows'] | undefined;
	extra?: React.ReactElement;
}) => {
	const { userInfo } = useAuth();
	const [isAmountNegative, setIsAmountNegative] = useState(false);

	return (
		<>
			<Tooltip
				title="You can search for a contract by entering vessel name, contract identifier or counterparty name"
				placement="right"
			>
				<Form.Item
					name="voyage"
					label="Contract"
					rules={required}
				>
					<Select<number>
						placeholder="Select Contract"
						showSearch
						optionLabelProp="identifier"
						filterOption={(inputValue, option) => {
							if (option == null) {
								return false;
							}

							const lowercaseInput = inputValue.toLowerCase();
							const { identifier, counterpartyName, vesselName } = option;

							return (
								identifier?.toLowerCase().includes(lowercaseInput) ||
								counterpartyName?.toLowerCase().includes(lowercaseInput) ||
								vesselName?.toLowerCase().includes(lowercaseInput)
							);
						}}
						options={(voyages ?? []).map((f) => {
							return {
								identifier: f.identifier,
								counterpartyName: f.counterpartyName,
								vesselName: f.vesselName,
								label: (
									<div className={styles.optionsRow}>
										<div className={classNames([
											styles.optionColumn,
											styles.optionLeft,
										])}
										>
											<strong>{f.identifier}</strong>
											{f.cpDate && (
												<span>
													<>
														CP:
														{' '}
														{toMoment(f.cpDate).format(DATE_AND_TIME)}
													</>
												</span>
											)}

										</div>
										<div className={classNames([
											styles.optionColumn,
											styles.optionRight,
										])}
										>
											<strong>{trimString(f.counterpartyName, 15, true)}</strong>
											<span>{f.vesselName}</span>
										</div>
									</div>
								),
								value: f.id,
							};
						})}
					/>
				</Form.Item>
			</Tooltip>
			<Form.Item
				name="customInvoiceId"
				label="Invoice ID"
			>
				<Input />
			</Form.Item>
			<Form.Item
				name="itemDescription"
				label="Item description"
			>
				<Input />
			</Form.Item>
			<AccountCodeOverrideFormItem />
			<Form.Item dependencies={['voyage']}>
				{() => {
					const selectedVoyageId = form.getFieldValue('voyage');
					const selectedVoyage = (voyages ?? []).find((v) => v.id === selectedVoyageId);

					return (
						<>
							<Form.Item
								name="amount"
								label="Invoice Amount"
							>
								<MultiCurrencyInput
									allowNegative
									baseCurrency={selectedVoyage?.currency || userInfo.baseCurrency}
									placeholder="Amount"
									onChange={(v) => setIsAmountNegative(v.value != null ? v.value < 0 : false)}
								/>
							</Form.Item>
							{isAmountNegative && (
								<Alert
									message={(
										<>
											<b>NOTE:</b>
											{' '}
											The amount being entered should only be negative for e.g. rebates.
										</>
									)}
								/>
							)}
						</>
					);
				}}
			</Form.Item>
			<Form.Item
				name="note"
				label="Internal note"
			>
				<Input.TextArea />
			</Form.Item>
			{extra}
			<Button
				htmlType="submit"
				type="primary"
			>
				Save Expense
			</Button>
		</>
	);
};

export default EuExpenseFormItems;
